/**
 * Pyodide Service (Singleton)
 *
 * Dynamically loads Pyodide from CDN and manages the Python execution environment.
 * Supports:
 * - Loading standard library + micropip
 * - Installing packages (pandas, numpy)
 * - Capturing stdout/stderr
 * - Extracting local variables (especially DataFrames)
 */

let pyodideInstance = null;
let pyodideLoadPromise = null;

const PYODIDE_CDN_URL = "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js";

/**
 * Loads Pyodide script and initializes the WASM environment.
 */
export async function loadPyodide() {
  if (pyodideInstance) return pyodideInstance;
  if (pyodideLoadPromise) return pyodideLoadPromise;

  pyodideLoadPromise = (async () => {
    // 1. Inject Script Tag
    if (!window.loadPyodide) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = PYODIDE_CDN_URL;
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Pyodide script"));
        document.head.appendChild(script);
      });
    }

    // 2. Initialize Pyodide
    const pyodide = await window.loadPyodide({
      indexURL: "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/",
      stdout: (text) => console.log("[Python Stdout]", text),
      stderr: (text) => console.warn("[Python Stderr]", text),
    });

    // 3. Load Essential Packages
    await pyodide.loadPackage(["micropip", "pandas", "numpy"]);

    // 4. Setup Custom Python Helpers
    // - _capture_stdout: redirects print() to a buffer
    // - _get_locals: returns a dict of local variables (JSON-serializable)
    await pyodide.runPythonAsync(`
import sys
import io
import json
import pandas as pd
import numpy as np

# Global buffer for stdout capture
class StdoutBuffer(io.StringIO):
    def __init__(self):
        super().__init__()
        self.output = ""

    def write(self, s):
        self.output += s
        # Also print to JS console for debugging
        # sys.__stdout__.write(s)

stdout_buffer = StdoutBuffer()

def _reset_buffer():
    stdout_buffer.output = ""
    # Redirect stdout
    sys.stdout = stdout_buffer

def _get_buffer():
    return stdout_buffer.output

def _serialize_dataframe(df):
    """Converts a DataFrame to a simple JSON-compatible dict."""
    try:
        # Simple serialization: records orientation
        # Handle NaN/Inf -> null (using replace or json dumps options)
        data = df.where(pd.notnull(df), None).to_dict(orient='records')
        columns = list(df.columns)
        return {"columns": columns, "data": data}
    except Exception as e:
        return {"error": str(e)}

def _get_locals_as_json():
    """Extracts local variables, converting DataFrames specifically."""
    result = {}
    # Filter out internal/module variables
    keys = [k for k in globals().keys() if not k.startswith('_') and k not in ['sys', 'io', 'json', 'pd', 'np', 'StdoutBuffer', 'stdout_buffer', '_reset_buffer', '_get_buffer', '_serialize_dataframe', '_get_locals_as_json']]

    for k in keys:
        val = globals()[k]
        if isinstance(val, pd.DataFrame):
            result[k] = {
                "type": "DataFrame",
                "value": _serialize_dataframe(val)
            }
        elif isinstance(val, (int, float, str, bool, list, dict)):
            try:
                # Ensure JSON serializable
                json.dumps(val)
                result[k] = {"type": type(val).__name__, "value": val}
            except:
                result[k] = {"type": "Unknown", "value": str(val)}
        else:
            result[k] = {"type": "Object", "value": str(val)}

    return json.dumps(result)

def _clear_globals():
    """Deletes all variables from the global scope except for the protected system helpers."""
    protected = {'sys', 'io', 'json', 'pd', 'np', 'StdoutBuffer', 'stdout_buffer', '_reset_buffer', '_get_buffer', '_serialize_dataframe', '_get_locals_as_json', '_clear_globals'}
    # list(globals().keys()) to avoid 'dictionary changed size during iteration' error
    for k in list(globals().keys()):
        if not k.startswith('_') and k not in protected:
            del globals()[k]
    `);

    pyodideInstance = pyodide;
    return pyodide;
  })();

  return pyodideLoadPromise;
}

/**
 * Runs Python code and returns the result.
 * @param {string} code - Python code to execute
 * @returns {Promise<{ stdout: string, variables: object, error: string }>}
 */
export async function runPythonCode(code) {
  try {
    const pyodide = await loadPyodide();

    // [Fix] Clear previous session's variables and reset stdout buffer
    pyodide.runPython("_clear_globals()");
    pyodide.runPython("_reset_buffer()");

    // Execute User Code
    await pyodide.runPythonAsync(code);

    // Capture Output
    const stdout = pyodide.runPython("_get_buffer()");

    // Extract Variables (JSON string -> JS Object)
    const varsJson = pyodide.runPython("_get_locals_as_json()");
    const variables = JSON.parse(varsJson);

    return { stdout, variables, error: null };

  } catch (err) {
    return {
      stdout: "",
      variables: {},
      error: err.message || String(err)
    };
  }
}

/**
 * Helper to check if Pyodide is ready.
 */
export function isPyodideReady() {
  return !!pyodideInstance;
}
